import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { clinicService, getLocalizedValue } from '@/services/api';
import { 
  Building2, 
  MapPin, 
  Phone, 
  Clock, 
  Star,
  ChevronLeft,
  Navigation,
  Calendar,
  Users,
  Stethoscope,
  ExternalLink,
  AlertCircle
} from 'lucide-react';

// Fallback mock clinic data when API unavailable
const getMockClinic = (lang) => ({
  id: 1,
  name: lang === 'ar' ? 'المركز الطبي الحديث' : 'Modern Medical Center',
  description: lang === 'ar' 
    ? 'مركز طبي متكامل يقدم خدمات طبية شاملة مع أحدث التقنيات والمعدات الطبية.'
    : 'A comprehensive medical center providing complete healthcare services with the latest technology and medical equipment.',
  image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=400&fit=crop',
  address: lang === 'ar' ? 'شارع المزة، دمشق، سوريا' : 'Mazzeh Street, Damascus, Syria',
  phone: '+963 11 123 4567',
  rating: 4.8,
  reviews_count: 256,
  working_hours: {
    weekdays: lang === 'ar' ? '8:00 ص - 10:00 م' : '8:00 AM - 10:00 PM',
    weekend: lang === 'ar' ? '9:00 ص - 6:00 م' : '9:00 AM - 6:00 PM',
  },
  specialties: [
    lang === 'ar' ? 'أمراض القلب' : 'Cardiology',
    lang === 'ar' ? 'طب الأعصاب' : 'Neurology',
    lang === 'ar' ? 'طب العيون' : 'Ophthalmology',
  ],
  doctors: [],
  facilities: [
    lang === 'ar' ? 'غرف طوارئ' : 'Emergency Rooms',
    lang === 'ar' ? 'مختبر تحاليل' : 'Laboratory',
    lang === 'ar' ? 'أشعة وتصوير' : 'Radiology',
    lang === 'ar' ? 'صيدلية' : 'Pharmacy',
  ],
  coordinates: { lat: 33.5138, lng: 36.2765 },
});

// Transform API clinic data to UI format
const transformClinic = (apiClinic, lang) => {
  if (!apiClinic) return null;
  
  return {
    id: apiClinic.id,
    name: getLocalizedValue(apiClinic.name, lang) || apiClinic.name,
    description: getLocalizedValue(apiClinic.description, lang) || apiClinic.description || '',
    image: apiClinic.image || apiClinic.media?.[0]?.url || 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&h=400&fit=crop',
    address: getLocalizedValue(apiClinic.address, lang) || apiClinic.address || '',
    phone: apiClinic.phone_number || apiClinic.phone || '',
    rating: parseFloat(apiClinic.rate) || parseFloat(apiClinic.rating) || 0,
    reviews_count: apiClinic.total_reviews || 0,
    working_hours: {
      weekdays: apiClinic.working_hours || (lang === 'ar' ? '8:00 ص - 10:00 م' : '8:00 AM - 10:00 PM'),
      weekend: apiClinic.weekend_hours || (lang === 'ar' ? '9:00 ص - 6:00 م' : '9:00 AM - 6:00 PM'),
    },
    specialties: (apiClinic.specialities || apiClinic.specialties || []).map(s => 
      getLocalizedValue(s.name, lang) || s.name
    ),
    doctors: [],
    facilities: apiClinic.facilities || [],
    coordinates: apiClinic.latitude && apiClinic.longitude 
      ? { lat: parseFloat(apiClinic.latitude), lng: parseFloat(apiClinic.longitude) }
      : null,
  };
};

// Transform API doctor data
const transformDoctor = (doctor, lang) => ({
  id: doctor.id,
  name: getLocalizedValue(doctor.name, lang) || `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim() || 'Doctor',
  specialty: doctor.specialities?.[0] 
    ? (getLocalizedValue(doctor.specialities[0].name, lang) || doctor.specialities[0].name)
    : (lang === 'ar' ? 'طبيب عام' : 'General'),
  image: doctor.image || doctor.media?.[0]?.url || `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.name?.en || 'D')}&background=0F6D77&color=fff`,
  rating: parseFloat(doctor.rate) || 0,
});

const ClinicDetailPage = () => {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'ar';

  const [clinic, setClinic] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingMockData, setIsUsingMockData] = useState(false);

  useEffect(() => {
    const fetchClinicData = async () => {
      setLoading(true);
      setError(null);
      setIsUsingMockData(false);

      try {
        // Fetch clinic details
        const clinicResponse = await clinicService.getById(id);
        const clinicData = clinicResponse.data?.data || clinicResponse.data;
        
        if (clinicData) {
          const transformedClinic = transformClinic(clinicData, lang);
          setClinic(transformedClinic);

          // Fetch doctors in this clinic
          try {
            const doctorsResponse = await clinicService.getDoctors(id);
            const doctorsData = doctorsResponse.data?.data || doctorsResponse.data || [];
            if (Array.isArray(doctorsData)) {
              setDoctors(doctorsData.slice(0, 5).map(d => transformDoctor(d, lang)));
            }
          } catch (docError) {
            console.log('Could not fetch clinic doctors:', docError);
          }
        } else {
          throw new Error('No clinic data');
        }
      } catch (err) {
        console.error('Failed to fetch clinic:', err);
        // Use mock data as fallback
        setClinic(getMockClinic(lang));
        setIsUsingMockData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchClinicData();
  }, [id, lang]);

  const openInMaps = () => {
    if (clinic?.coordinates) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${clinic.coordinates.lat},${clinic.coordinates.lng}`,
        '_blank'
      );
    } else if (clinic?.address) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clinic.address)}`,
        '_blank'
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 py-6">
          <Skeleton className="h-64 w-full rounded-xl mb-6" />
          <Skeleton className="h-8 w-1/2 mb-4" />
          <Skeleton className="h-4 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Card className="max-w-md dark:bg-slate-800">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'العيادة غير موجودة' : 'Clinic not found'}</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              {lang === 'ar' ? 'رجوع' : 'Go Back'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="clinic-detail-page">
      {/* Mock Data Notice */}
      {isUsingMockData && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-2">
          <div className="container mx-auto flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm">
            <AlertCircle className="h-4 w-4" />
            {lang === 'ar' 
              ? 'عرض بيانات تجريبية - سيتم تحديث البيانات عند توفر واجهة برمجة التطبيقات'
              : 'Showing demo data - Will update when API becomes available'}
          </div>
        </div>
      )}

      {/* Hero Image */}
      <div className="relative h-64 md:h-80">
        <img 
          src={clinic.image} 
          alt={clinic.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 left-4 bg-white/20 backdrop-blur hover:bg-white/30 text-white"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
      </div>

      <div className="container mx-auto px-4 -mt-20 relative z-10 pb-8">
        {/* Main Info Card */}
        <Card className="dark:bg-slate-800 border-0 shadow-lg mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{clinic.name}</h1>
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 mb-3">
                  <MapPin className="h-4 w-4" />
                  <span>{clinic.address || (lang === 'ar' ? 'العنوان غير متوفر' : 'Address not available')}</span>
                </div>
                {clinic.rating > 0 && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
                      <span className="font-semibold">{clinic.rating.toFixed(1)}</span>
                      <span className="text-slate-500 dark:text-slate-400">
                        ({clinic.reviews_count} {lang === 'ar' ? 'تقييم' : 'reviews'})
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={openInMaps} className="dark:border-slate-600">
                  <Navigation className="h-4 w-4 mr-2" />
                  {lang === 'ar' ? 'الاتجاهات' : 'Directions'}
                </Button>
                {clinic.phone && (
                  <Button className="bg-teal-600 hover:bg-teal-700" asChild>
                    <a href={`tel:${clinic.phone}`}>
                      <Phone className="h-4 w-4 mr-2" />
                      {lang === 'ar' ? 'اتصل' : 'Call'}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="md:col-span-2 space-y-6">
            {/* About */}
            {clinic.description && (
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg dark:text-white">
                    {lang === 'ar' ? 'عن المركز' : 'About'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 dark:text-slate-400">{clinic.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Doctors */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                  <Users className="h-5 w-5" />
                  {lang === 'ar' ? 'الأطباء' : 'Doctors'}
                </CardTitle>
                <Button variant="link" className="text-teal-600" asChild>
                  <Link to={`/doctors?clinic_id=${id}`}>
                    {lang === 'ar' ? 'عرض الكل' : 'View All'}
                    <ChevronLeft className="h-4 w-4 mr-1 rotate-180" />
                  </Link>
                </Button>
              </CardHeader>
              <CardContent>
                {doctors.length > 0 ? (
                  <div className="space-y-4">
                    {doctors.map(doctor => (
                      <Link 
                        key={doctor.id} 
                        to={`/doctors/${doctor.id}`}
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                      >
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={doctor.image} />
                          <AvatarFallback className="bg-teal-600 text-white">
                            {doctor.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-medium text-slate-900 dark:text-white">{doctor.name}</h3>
                          <p className="text-sm text-teal-600">{doctor.specialty}</p>
                        </div>
                        {doctor.rating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                            <span className="text-sm font-medium">{doctor.rating.toFixed(1)}</span>
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 dark:text-slate-400 text-center py-4">
                    {lang === 'ar' ? 'لا يوجد أطباء متاحين حالياً' : 'No doctors available at the moment'}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Facilities */}
            {clinic.facilities && clinic.facilities.length > 0 && (
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                    <Building2 className="h-5 w-5" />
                    {lang === 'ar' ? 'المرافق' : 'Facilities'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {clinic.facilities.map((facility, index) => (
                      <Badge key={index} variant="secondary" className="px-3 py-1">
                        {typeof facility === 'string' ? facility : getLocalizedValue(facility.name, lang)}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Working Hours */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                  <Clock className="h-5 w-5" />
                  {lang === 'ar' ? 'ساعات العمل' : 'Working Hours'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">
                    {lang === 'ar' ? 'السبت - الخميس' : 'Sat - Thu'}
                  </span>
                  <span className="font-medium dark:text-white">{clinic.working_hours.weekdays}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">
                    {lang === 'ar' ? 'الجمعة' : 'Friday'}
                  </span>
                  <span className="font-medium dark:text-white">{clinic.working_hours.weekend}</span>
                </div>
              </CardContent>
            </Card>

            {/* Specialties */}
            {clinic.specialties && clinic.specialties.length > 0 && (
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                    <Stethoscope className="h-5 w-5" />
                    {lang === 'ar' ? 'التخصصات' : 'Specialties'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {clinic.specialties.map((specialty, index) => (
                      <div key={index} className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                        {specialty}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact */}
            {clinic.phone && (
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                    <Phone className="h-5 w-5" />
                    {lang === 'ar' ? 'التواصل' : 'Contact'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a 
                    href={`tel:${clinic.phone}`}
                    className="flex items-center gap-2 text-teal-600 hover:underline"
                  >
                    {clinic.phone}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </CardContent>
              </Card>
            )}

            {/* Book Appointment CTA */}
            <Button className="w-full bg-teal-600 hover:bg-teal-700" size="lg" asChild>
              <Link to="/doctors">
                <Calendar className="h-4 w-4 mr-2" />
                {lang === 'ar' ? 'احجز موعد' : 'Book Appointment'}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicDetailPage;
